import {announce} from '@github-ui/aria-live'
import {INCREASE_INDIVIDUAL_BUDGET_URL} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {Banner, Link, LinkButton} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'
import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions-types/agent-quota-snapshot'
import styles from './QuotaBanner.module.css'
import {
  getQuotaBannerContent,
  getQuotaBannerDescription,
  getQuotaBannerState,
  isBlockingQuotaState,
  isHighSeverityApproachingLimit,
} from '../../utils/quota-banner-state'

type QuotaBannerProps = {
  agentQuotaSnapshot?: AgentQuotaSnapshot | null
}

// Pass an element so the ariaNotify polyfill can locate the correct live
// region root when the banner renders inside a dialog (the polyfill suppresses
// announcements from elements outside the active modal).
function useAnnounceMessage(message: string | null, assertive: boolean, element: HTMLElement | null) {
  useEffect(() => {
    if (message && element) {
      announce(message, {assertive, element})
    }
  }, [message, assertive, element])
}

export function QuotaBanner({agentQuotaSnapshot}: QuotaBannerProps) {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((node: HTMLDivElement | null) => setContainerEl(node), [])

  const state = getQuotaBannerState(agentQuotaSnapshot ?? undefined)
  const description = getQuotaBannerDescription(state)
  const content = getQuotaBannerContent(state)
  const useIndividualBudgetCta = Boolean(
    agentQuotaSnapshot &&
    copilotFeatureFlags.ipmUbbIndividualBudgetBanner &&
    agentQuotaSnapshot.is_paid_individual === true &&
    (state.kind === 'cfiBlocked' || state.kind === 'cfiBudgetHit'),
  )
  const title = content.title
  const primaryAction = useIndividualBudgetCta
    ? {label: 'Increase your budget', href: INCREASE_INDIVIDUAL_BUDGET_URL}
    : content.primaryAction
  const upgradeAction = useIndividualBudgetCta ? undefined : content.upgradeAction

  // Announce the same copy the banner renders, so screen-reader users hear the
  // dynamic "used X%" value instead of a coarse static bucket. Blocked states
  // are assertive (the submit action is also disabled); the approaching-limit
  // warning is announced politely.
  useAnnounceMessage(description || null, isBlockingQuotaState(state), containerEl)

  if (!agentQuotaSnapshot || state.kind === 'none') return null

  // Blocked states are critical; approaching-limit escalates info → warning.
  let variant: 'critical' | 'warning' | 'info' = 'critical'
  if (state.kind === 'approachingLimit') {
    variant = isHighSeverityApproachingLimit(state.percentUsed) ? 'warning' : 'info'
  }

  return (
    <div ref={containerRef} className={styles.container} data-testid="quota-banner">
      <Banner
        variant={variant}
        title={title}
        hideTitle
        description={description}
        actionsLayout={upgradeAction ? 'stacked' : 'default'}
        primaryAction={
          primaryAction ? (
            <LinkButton as="a" href={primaryAction.href}>
              {primaryAction.label}
            </LinkButton>
          ) : undefined
        }
        secondaryAction={
          upgradeAction ? (
            <Link as="a" href={upgradeAction.href}>
              {upgradeAction.label}
            </Link>
          ) : undefined
        }
      />
    </div>
  )
}
